import React, { memo, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { Dropdown, SlideUp, Text } from 'common-components'
import { useDropdownState } from 'hooks/useDropdownState'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { dispatch } from 'store'
import { IModelDropdownProps } from './types'
import { useEventListener } from 'hooks'
import { useScrollableElement } from '../../../../context'
import ModelDropdownContent from './ModelDropdownContent'
import { userSelectors } from 'store/features/user/selectors'
import { isPremiumModel, handlePremiumModelClick } from 'utils'
import CrownIcon from 'assets/svgr-icons/Crown.svg'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import {
  LabelWrapper,
  CrownBadgeIcon
} from 'containers/SubSidebar/shared/GroupedModelDropdown'

const ModelDropdown: React.FC<IModelDropdownProps> = ({
  models,
  selectedModelCode,
  onModelSelect,
  onOpenChange,
  className = ''
}) => {
  const { scrollableElementRef } = useScrollableElement()
  const [isInnerSlideUpOpen, setIsInnerSlideUpOpen] = useState(false)

  const { isOpen, closeDropdown, toggleDropdown } = useDropdownState(
    () => {
      dispatch(editorThunks.setIsMobileInnerSliderOpen(true))
      onOpenChange?.(true)
    },
    () => {
      dispatch(editorThunks.setIsMobileInnerSliderOpen(false))
      setIsInnerSlideUpOpen(false)
      onOpenChange?.(false)
    }
  )

  const isTablet = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const userCurrentPlanId = useSelector(userSelectors.userCurrentPlanId)

  const handleSelect = useCallback(
    (modelCode: string) => {
      const model = models.find(m => m.model === modelCode)

      if (handlePremiumModelClick(model, userCurrentPlanId)) {
        return
      }
      if (modelCode !== selectedModelCode) {
        onModelSelect(modelCode)
      }
      closeDropdown()
    },
    [models, userCurrentPlanId, selectedModelCode, onModelSelect, closeDropdown]
  )

  useEventListener(
    'scroll',
    closeDropdown,
    !isTablet ? scrollableElementRef.current : null,
    true
  )

  const selectedModel = models.find(model => model.model === selectedModelCode)
  const displayValue =
    selectedModel?.name || translate(T.SELECT_MODEL_PLACEHOLDER)
  const isSelectedModelPremium = selectedModel
    ? isPremiumModel(selectedModel, userCurrentPlanId)
    : false

  if (isTablet && isOpen) {
    return createPortal(
      <SlideUp
        isOpen={isOpen}
        onClose={closeDropdown}
        isHidden={isInnerSlideUpOpen}
      >
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={handleSelect}
          userCurrentPlanId={userCurrentPlanId}
          onInnerSlideUpOpenChange={setIsInnerSlideUpOpen}
        />
      </SlideUp>,
      document.body
    )
  }

  return (
    <Dropdown
      className={className}
      headerContent={
        <LabelWrapper>
          <Text
            type="h2"
            color={selectedModel ? 'secondaryDark' : 'secondarySemiLight'}
          >
            {displayValue}
          </Text>
          {isSelectedModelPremium && (
            <CrownBadgeIcon
              icon={<CrownIcon />}
              size="small"
              color="goldDark"
            />
          )}
        </LabelWrapper>
      }
      mainContent={
        <ModelDropdownContent
          models={models}
          selectedModelCode={selectedModelCode}
          onSelect={handleSelect}
          userCurrentPlanId={userCurrentPlanId}
        />
      }
      isOpen={isOpen}
      toggleDropdown={toggleDropdown}
      closeDropdown={closeDropdown}
      forceBottom
    />
  )
}

export default memo(ModelDropdown)
